import type {
    Bi,
    Gana,
    Lakara,
    Pada,
    PadaClass,
    ReviewStatus,
    VerbTable
} from '@/sanskrit/lib/sanskrit/types'
import type { SourceId } from '@/sanskrit/lib/sanskrit/sources'

import { tbl } from '@/sanskrit/lib/sanskrit/verb-engine'

export interface DhatuExample {
    sa: string
    hi: string
    en: string
}

export interface Derivation {
    expr: string
    note: Bi
}

export interface Dhatu {
    id: string
    root: string
    iast: string
    meaning: Bi
    /** Lat prathama eka citation, e.g. पठति */
    citation: string
    dhatupatha?: string
    gana: Gana
    padaClass: PadaClass
    it?: 'set' | 'anit' | 'vet'
    core: boolean
    upasarga?: string
    baseRootId?: string
    present?: Partial<Record<Pada, string[]>>
    imperfect?: Partial<Record<Pada, string[]>>
    future?: string[]
    conditional?: string[]
    lut?: string[]
    presentDerivation?: Derivation
    futureDerivation?: Derivation
    explicit?: Partial<Record<Lakara, Partial<Record<Pada, VerbTable>>>>
    explicitStatus?: Partial<Record<Lakara, ReviewStatus>>
    via?: Partial<Record<Lakara, { dhatuId: string; note: Bi }>>
    related: Array<{ sa: string; meaning: Bi }>
    examples: DhatuExample[]
    notes: Bi[]
    status: ReviewStatus
    sources: SourceId[]
}

const CORE_SOURCES: SourceId[] = ['dhatupatha', 'lsk', 'kale', 'macdonell']

const bhvadiNote = (expr: string): Derivation => ({
    expr,
    note: {
        hi: 'भ्वादिगण: लट्, लोट्, लङ्, विधिलिङ् में धातु के बाद विकरण "शप्" (अ) जुड़ता है (कर्तरि शप् 3.1.68)।',
        en: 'Class 1 (bhvādi): in the present-system lakāras the class marker शप् (अ) is added after the root (कर्तरि शप् 3.1.68).'
    }
})

const setFuture = (expr: string): Derivation => ({
    expr,
    note: {
        hi: 'सेट् धातु: भविष्यत् चिह्न स्य से पहले इ (इट्) आता है, और इ के बाद स् का ष् हो जाता है (आदेशप्रत्यययोः 8.3.59)।',
        en: 'A seṭ root: the future marker स्य takes a connecting इ, and स् after इ becomes ष् (आदेशप्रत्यययोः 8.3.59).'
    }
})

const anitFuture = (expr: string, hi: string, en: string): Derivation => ({
    expr,
    note: { hi: `अनिट् धातु: स्य सीधे जुड़ता है। ${hi}`, en: `An aniṭ root: स्य attaches directly. ${en}` }
})

export const DHATUS: Dhatu[] = [
    {
        id: 'path',
        root: 'पठ्',
        iast: 'paṭh',
        meaning: { hi: 'पढ़ना', en: 'to read, to recite' },
        citation: 'पठति',
        dhatupatha: 'पठ व्यक्तायां वाचि',
        gana: 1,
        padaClass: 'parasmaipada',
        it: 'set',
        core: true,
        present: { P: ['पठ'] },
        future: ['पठिष्य'],
        lut: ['पठित'],
        presentDerivation: bhvadiNote('पठ् + अ'),
        futureDerivation: setFuture('पठ् + इ + स्य'),
        explicit: {
            lit: { P: tbl('पपाठ', 'पेठतुः', 'पेठुः', 'पेठिथ', 'पेठथुः', 'पेठ', 'पपाठ/पपठ', 'पेठिव', 'पेठिम') },
            lun: { P: tbl('अपाठीत्/अपठीत्', 'अपाठिष्टाम्/अपठिष्टाम्', 'अपाठिषुः/अपठिषुः', 'अपाठीः/अपठीः', 'अपाठिष्टम्/अपठिष्टम्', 'अपाठिष्ट/अपठिष्ट', 'अपाठिषम्/अपठिषम्', 'अपाठिष्व/अपठिष्व', 'अपाठिष्म/अपठिष्म') },
            ashirlin: { P: tbl('पठ्यात्', 'पठ्यास्ताम्', 'पठ्यासुः', 'पठ्याः', 'पठ्यास्तम्', 'पठ्यास्त', 'पठ्यासम्', 'पठ्यास्व', 'पठ्यास्म') }
        },
        explicitStatus: { lit: 'verified', lun: 'verified', ashirlin: 'verified' },
        related: [
            { sa: 'पाठः', meaning: { hi: 'पाठ', en: 'lesson' } },
            { sa: 'पाठकः', meaning: { hi: 'पढ़ने वाला', en: 'reader' } },
            { sa: 'पठनम्', meaning: { hi: 'पढ़ना', en: 'reading' } },
            { sa: 'पाठशाला', meaning: { hi: 'विद्यालय', en: 'school' } }
        ],
        examples: [
            { sa: 'छात्राः पाठं पठन्ति।', hi: 'छात्र पाठ पढ़ते हैं।', en: 'The students read the lesson.' },
            { sa: 'अहं संस्कृतं पठामि।', hi: 'मैं संस्कृत पढ़ता/पढ़ती हूँ।', en: 'I study Sanskrit.' },
            { sa: 'श्वः त्वं किं पठिष्यसि?', hi: 'कल तुम क्या पढ़ोगे?', en: 'What will you read tomorrow?' }
        ],
        notes: [
            {
                hi: 'पूर्णतः नियमित भ्वादि धातु: नए विद्यार्थियों के लिए आदर्श उदाहरण।',
                en: 'A perfectly regular class 1 root, the model for beginners.'
            },
            {
                hi: 'लुङ् में दो स्वीकृत रूप हैं: अपाठीत् और अपठीत् (अतो हलादेर्लघोः 7.2.7 से वृद्धि वैकल्पिक)।',
                en: 'Its aorist has two accepted forms, अपाठीत् and अपठीत्, because vṛddhi is optional here (अतो हलादेर्लघोः 7.2.7).'
            }
        ],
        status: 'verified',
        sources: CORE_SOURCES
    },
    {
        id: 'likh',
        root: 'लिख्',
        iast: 'likh',
        meaning: { hi: 'लिखना', en: 'to write' },
        citation: 'लिखति',
        dhatupatha: 'लिख अक्षरविन्यासे',
        gana: 6,
        padaClass: 'parasmaipada',
        it: 'set',
        core: true,
        present: { P: ['लिख'] },
        future: ['लेखिष्य'],
        lut: ['लेखित'],
        presentDerivation: {
            expr: 'लिख् + अ',
            note: {
                hi: 'तुदादिगण: विकरण "श" (अ) जुड़ता है और धातु के स्वर का गुण नहीं होता। इसलिए लेखति नहीं, लिखति।',
                en: 'Class 6 (tudādi): the marker श (अ) is added and the root vowel does NOT take guṇa. So it is लिखति, not लेखति.'
            }
        },
        futureDerivation: {
            expr: 'लिख् + इ + स्य',
            note: {
                hi: 'भविष्यत् में धातु के इ का गुण ए होता है: लेख् + इष्य = लेखिष्य।',
                en: 'In the future the root vowel इ takes guṇa ए: लेख् + इष्य = लेखिष्य.'
            }
        },
        explicit: {
            lit: { P: tbl('लिलेख', 'लिलिखतुः', 'लिलिखुः', 'लिलेखिथ', 'लिलिखथुः', 'लिलिख', 'लिलेख', 'लिलिखिव', 'लिलिखिम') },
            lun: { P: tbl('अलेखीत्', 'अलेखिष्टाम्', 'अलेखिषुः', 'अलेखीः', 'अलेखिष्टम्', 'अलेखिष्ट', 'अलेखिषम्', 'अलेखिष्व', 'अलेखिष्म') },
            ashirlin: { P: tbl('लिख्यात्', 'लिख्यास्ताम्', 'लिख्यासुः', 'लिख्याः', 'लिख्यास्तम्', 'लिख्यास्त', 'लिख्यासम्', 'लिख्यास्व', 'लिख्यास्म') }
        },
        explicitStatus: { lit: 'verified', lun: 'verified', ashirlin: 'verified' },
        related: [
            { sa: 'लेखः', meaning: { hi: 'लेख', en: 'writing, article' } },
            { sa: 'लेखकः', meaning: { hi: 'लेखक', en: 'writer' } },
            { sa: 'लेखनी', meaning: { hi: 'कलम', en: 'pen' } },
            { sa: 'लिखितम्', meaning: { hi: 'लिखा हुआ', en: 'written' } }
        ],
        examples: [
            { sa: 'बालिका पत्रं लिखति।', hi: 'बालिका पत्र लिखती है।', en: 'The girl writes a letter.' },
            { sa: 'वयं लेखन्या लिखामः।', hi: 'हम कलम से लिखते हैं।', en: 'We write with a pen.' },
            { sa: 'सः कथाम् अलिखत्।', hi: 'उसने कहानी लिखी।', en: 'He wrote a story.' }
        ],
        notes: [
            {
                hi: 'भ्वादि नहीं, तुदादि: इसलिए वर्तमान अङ्ग में गुण नहीं होता।',
                en: 'Class 6, not class 1, so the present stem has no guṇa.'
            }
        ],
        status: 'verified',
        sources: CORE_SOURCES
    },
    {
        id: 'gam',
        root: 'गम्',
        iast: 'gam',
        meaning: { hi: 'जाना', en: 'to go' },
        citation: 'गच्छति',
        dhatupatha: 'गम्लृ गतौ',
        gana: 1,
        padaClass: 'parasmaipada',
        it: 'anit',
        core: true,
        present: { P: ['गच्छ'] },
        future: ['गमिष्य'],
        lut: ['गन्त'],
        presentDerivation: {
            expr: 'गम् → गच्छ',
            note: {
                hi: 'विशेष अङ्ग: लट्, लोट्, लङ्, विधिलिङ् में म् के स्थान पर छ आता है और अङ्ग गच्छ बनता है (इषुगमियमां छः 7.3.77)। फिर सामान्य अ-प्रत्यय लगते हैं।',
                en: 'Special stem: in the present-system lakāras the म् is replaced by छ, giving the stem गच्छ (इषुगमियमां छः 7.3.77). The normal endings then attach to गच्छ.'
            }
        },
        futureDerivation: {
            expr: 'गम् + इ + स्य',
            note: {
                hi: 'गम् अनिट् है, फिर भी परस्मैपद में स्य से पहले इ आता है (गमेरिट् परस्मैपदेषु 7.2.58): गमिष्यति।',
                en: 'गम् is normally aniṭ, but in parasmaipada the future takes इ (गमेरिट् परस्मैपदेषु 7.2.58): गमिष्यति.'
            }
        },
        explicit: {
            lit: { P: tbl('जगाम', 'जग्मतुः', 'जग्मुः', 'जगमिथ/जगन्थ', 'जग्मथुः', 'जग्म', 'जगाम/जगम', 'जग्मिव', 'जग्मिम') },
            lun: { P: tbl('अगमत्', 'अगमताम्', 'अगमन्', 'अगमः', 'अगमतम्', 'अगमत', 'अगमम्', 'अगमाव', 'अगमाम') },
            ashirlin: { P: tbl('गम्यात्', 'गम्यास्ताम्', 'गम्यासुः', 'गम्याः', 'गम्यास्तम्', 'गम्यास्त', 'गम्यासम्', 'गम्यास्व', 'गम्यास्म') }
        },
        explicitStatus: { lit: 'verified', lun: 'verified', ashirlin: 'verified' },
        related: [
            { sa: 'गमनम्', meaning: { hi: 'जाना', en: 'going' } },
            { sa: 'गतिः', meaning: { hi: 'गति', en: 'motion' } },
            { sa: 'आगमनम्', meaning: { hi: 'आना', en: 'arrival' } },
            { sa: 'गन्तव्यम्', meaning: { hi: 'जहाँ जाना है', en: 'destination' } }
        ],
        examples: [
            { sa: 'बालकः विद्यालयं गच्छति।', hi: 'बालक विद्यालय जाता है।', en: 'The boy goes to school.' },
            { sa: 'वयं ग्रामं गच्छामः।', hi: 'हम गाँव जाते हैं।', en: 'We go to the village.' },
            { sa: 'त्वं कुत्र गमिष्यसि?', hi: 'तुम कहाँ जाओगे?', en: 'Where will you go?' }
        ],
        notes: [
            {
                hi: 'वर्तमान में अङ्ग गच्छ है, पर भविष्यत् (गमिष्यति) और लुङ् (अगमत्) में मूल गम् ही दिखता है।',
                en: 'The present stem is गच्छ, but the future (गमिष्यति) and aorist (अगमत्) are built on the bare root गम्.'
            },
            {
                hi: 'जाने का स्थान द्वितीया में आता है: ग्रामं गच्छति।',
                en: 'The destination takes द्वितीया: ग्रामं गच्छति (goes to the village).'
            }
        ],
        status: 'verified',
        sources: CORE_SOURCES
    },
    {
        id: 'bhu',
        root: 'भू',
        iast: 'bhū',
        meaning: { hi: 'होना', en: 'to be, to become' },
        citation: 'भवति',
        dhatupatha: 'भू सत्तायाम्',
        gana: 1,
        padaClass: 'parasmaipada',
        it: 'set',
        core: true,
        present: { P: ['भव'] },
        future: ['भविष्य'],
        lut: ['भवित'],
        presentDerivation: {
            expr: 'भू + अ → भो + अ → भव',
            note: {
                hi: 'भ्वादिगण: शप् (अ) से पहले ऊ का गुण ओ होता है, और ओ + अ मिलकर अव बनता है (एचोऽयवायावः 6.1.78)।',
                en: 'Class 1: before शप् (अ) the ऊ takes guṇa ओ, and ओ + अ becomes अव (एचोऽयवायावः 6.1.78).'
            }
        },
        futureDerivation: setFuture('भू + इ + स्य → भो + इष्य → भविष्य'),
        explicit: {
            lit: { P: tbl('बभूव', 'बभूवतुः', 'बभूवुः', 'बभूविथ', 'बभूवथुः', 'बभूव', 'बभूव', 'बभूविव', 'बभूविम') },
            lun: { P: tbl('अभूत्', 'अभूताम्', 'अभूवन्', 'अभूः', 'अभूतम्', 'अभूत', 'अभूवम्', 'अभूव', 'अभूम') },
            ashirlin: { P: tbl('भूयात्', 'भूयास्ताम्', 'भूयासुः', 'भूयाः', 'भूयास्तम्', 'भूयास्त', 'भूयासम्', 'भूयास्व', 'भूयास्म') }
        },
        explicitStatus: { lit: 'verified', lun: 'verified', ashirlin: 'verified' },
        related: [
            { sa: 'भवनम्', meaning: { hi: 'भवन', en: 'building' } },
            { sa: 'भूमिः', meaning: { hi: 'धरती', en: 'earth' } },
            { sa: 'भावः', meaning: { hi: 'भाव', en: 'state, feeling' } },
            { sa: 'भूतम्', meaning: { hi: 'जो हो चुका', en: 'what has been' } }
        ],
        examples: [
            { sa: 'आकाशे मेघाः भवन्ति।', hi: 'आकाश में बादल होते हैं।', en: 'There are clouds in the sky.' },
            { sa: 'सर्वे सुखिनः भवन्तु।', hi: 'सब सुखी हों।', en: 'May all be happy.' },
            { sa: 'त्वं विद्वान् भविष्यसि।', hi: 'तुम विद्वान् बनोगे।', en: 'You will become learned.' }
        ],
        notes: [
            {
                hi: 'लघुसिद्धान्तकौमुदी में धातुरूप-प्रकरण इसी धातु से आरम्भ होता है।',
                en: 'Traditional verb chapters begin with this root.'
            },
            {
                hi: 'आर्धधातुक लकारों (लिट्, लुट्, लृट्, लुङ्, आशीर्लिङ्, लृङ्) में अस् धातु के स्थान पर भू आता है।',
                en: 'In the non-present lakāras, the root अस् is replaced by भू.'
            }
        ],
        status: 'verified',
        sources: CORE_SOURCES
    },
    {
        id: 'as',
        root: 'अस्',
        iast: 'as',
        meaning: { hi: 'होना (है)', en: 'to be, to exist' },
        citation: 'अस्ति',
        dhatupatha: 'अस भुवि',
        gana: 2,
        padaClass: 'parasmaipada',
        core: true,
        presentDerivation: {
            expr: 'अस् (कोई विकरण नहीं)',
            note: {
                hi: 'अदादिगण: धातु और प्रत्यय के बीच कोई विकरण नहीं आता। द्विवचन और बहुवचन में अ लुप्त हो जाता है: स्तः, सन्ति (श्नसोरल्लोपः 6.4.111)।',
                en: 'Class 2 (adādi): the ending joins the root directly. In the dual and plural the अ drops: स्तः, सन्ति (श्नसोरल्लोपः 6.4.111).'
            }
        },
        explicit: {
            lat: { P: tbl('अस्ति', 'स्तः', 'सन्ति', 'असि', 'स्थः', 'स्थ', 'अस्मि', 'स्वः', 'स्मः') },
            lan: { P: tbl('आसीत्', 'आस्ताम्', 'आसन्', 'आसीः', 'आस्तम्', 'आस्त', 'आसम्', 'आस्व', 'आस्म') },
            lot: { P: tbl('अस्तु/स्तात्', 'स्ताम्', 'सन्तु', 'एधि/स्तात्', 'स्तम्', 'स्त', 'असानि', 'असाव', 'असाम') },
            vidhilin: { P: tbl('स्यात्', 'स्याताम्', 'स्युः', 'स्याः', 'स्यातम्', 'स्यात', 'स्याम्', 'स्याव', 'स्याम') }
        },
        explicitStatus: { lat: 'verified', lan: 'verified', lot: 'verified', vidhilin: 'verified' },
        via: {
            lrt: { dhatuId: 'bhu', note: { hi: 'अस् के स्थान पर भू (अस्तेर्भूः 2.4.52): भविष्यति।', en: 'अस् is replaced by भू here (अस्तेर्भूः 2.4.52): भविष्यति.' } },
            lrn: { dhatuId: 'bhu', note: { hi: 'अस् के स्थान पर भू (अस्तेर्भूः 2.4.52)।', en: 'अस् is replaced by भू here (अस्तेर्भूः 2.4.52).' } },
            lut: { dhatuId: 'bhu', note: { hi: 'अस् के स्थान पर भू (अस्तेर्भूः 2.4.52)।', en: 'अस् is replaced by भू here (अस्तेर्भूः 2.4.52).' } },
            lit: { dhatuId: 'bhu', note: { hi: 'अस् के स्थान पर भू (अस्तेर्भूः 2.4.52): बभूव।', en: 'अस् is replaced by भू here (अस्तेर्भूः 2.4.52): बभूव.' } },
            lun: { dhatuId: 'bhu', note: { hi: 'अस् के स्थान पर भू (अस्तेर्भूः 2.4.52): अभूत्।', en: 'अस् is replaced by भू here (अस्तेर्भूः 2.4.52): अभूत्.' } },
            ashirlin: { dhatuId: 'bhu', note: { hi: 'अस् के स्थान पर भू (अस्तेर्भूः 2.4.52): भूयात्।', en: 'अस् is replaced by भू here (अस्तेर्भूः 2.4.52): भूयात्.' } }
        },
        related: [
            { sa: 'सत्यम्', meaning: { hi: 'सत्य (जो है)', en: 'truth (that which is)' } },
            { sa: 'सत्', meaning: { hi: 'सत्, अस्तित्व', en: 'being, real' } }
        ],
        examples: [
            { sa: 'एतत् मम गृहम् अस्ति।', hi: 'यह मेरा घर है।', en: 'This is my house.' },
            { sa: 'अहं छात्रः अस्मि।', hi: 'मैं छात्र हूँ।', en: 'I am a student.' },
            { sa: 'वृक्षे खगाः सन्ति।', hi: 'पेड़ पर पक्षी हैं।', en: 'There are birds in the tree.' }
        ],
        notes: [
            {
                hi: 'विशेष धातु: इसके रूप नियम से "बनाए" नहीं जाते, याद किए जाते हैं।',
                en: 'An irregular root: its forms are learned as they are, not built from the regular pattern.'
            },
            {
                hi: 'लङ् में आट् आगम और ईट्: आसीत्, आसीः।',
                en: 'Its imperfect takes the augment आ and an inserted ई: आसीत्, आसीः.'
            }
        ],
        status: 'verified',
        sources: CORE_SOURCES
    },
    {
        id: 'has',
        root: 'हस्',
        iast: 'has',
        meaning: { hi: 'हँसना', en: 'to laugh' },
        citation: 'हसति',
        dhatupatha: 'हसे हसने',
        gana: 1,
        padaClass: 'parasmaipada',
        it: 'set',
        core: true,
        present: { P: ['हस'] },
        future: ['हसिष्य'],
        lut: ['हसित'],
        presentDerivation: bhvadiNote('हस् + अ'),
        futureDerivation: setFuture('हस् + इ + स्य'),
        related: [
            { sa: 'हासः', meaning: { hi: 'हँसी', en: 'laughter' } },
            { sa: 'हास्यम्', meaning: { hi: 'हास्य', en: 'humour' } },
            { sa: 'उपहासः', meaning: { hi: 'मज़ाक उड़ाना', en: 'mockery' } }
        ],
        examples: [
            { sa: 'शिशुः हसति।', hi: 'शिशु हँसता है।', en: 'The baby laughs.' },
            { sa: 'किमर्थं हससि?', hi: 'तुम क्यों हँसते हो?', en: 'Why do you laugh?' },
            { sa: 'बालिकाः अहसन्।', hi: 'बालिकाएँ हँसीं।', en: 'The girls laughed.' }
        ],
        notes: [],
        status: 'verified',
        sources: CORE_SOURCES
    },
    {
        id: 'khad',
        root: 'खाद्',
        iast: 'khād',
        meaning: { hi: 'खाना', en: 'to eat' },
        citation: 'खादति',
        dhatupatha: 'खादृ भक्षणे',
        gana: 1,
        padaClass: 'parasmaipada',
        it: 'set',
        core: true,
        present: { P: ['खाद'] },
        future: ['खादिष्य'],
        lut: ['खादित'],
        presentDerivation: bhvadiNote('खाद् + अ'),
        futureDerivation: setFuture('खाद् + इ + स्य'),
        related: [
            { sa: 'खाद्यम्', meaning: { hi: 'खाने योग्य', en: 'food, edible' } },
            { sa: 'खादनम्', meaning: { hi: 'खाना', en: 'eating' } }
        ],
        examples: [
            { sa: 'वानरः फलं खादति।', hi: 'बंदर फल खाता है।', en: 'The monkey eats a fruit.' },
            { sa: 'वयं रोटिकां खादामः।', hi: 'हम रोटी खाते हैं।', en: 'We eat flatbread.' },
            { sa: 'अश्वः तृणं खादति।', hi: 'घोड़ा घास खाता है।', en: 'The horse eats grass.' }
        ],
        notes: [],
        status: 'verified',
        sources: CORE_SOURCES
    },
    {
        id: 'pac',
        root: 'पच्',
        iast: 'pac',
        meaning: { hi: 'पकाना', en: 'to cook' },
        citation: 'पचति',
        dhatupatha: 'डुपचँष् पाके',
        gana: 1,
        padaClass: 'ubhayapada',
        it: 'anit',
        core: true,
        present: { P: ['पच'], A: ['पच'] },
        future: ['पक्ष्य'],
        lut: ['पक्त'],
        presentDerivation: bhvadiNote('पच् + अ'),
        futureDerivation: anitFuture(
            'पच् + स्य → पक् + ष्य',
            'च् का क् (चोः कुः 8.2.30) और स् का ष् होकर क्ष् बनता है: पक्ष्यति।',
            'च् becomes क् (चोः कुः 8.2.30) and स् becomes ष्, giving क्ष्: पक्ष्यति.'
        ),
        related: [
            { sa: 'पाकः', meaning: { hi: 'पकाना, रसोई', en: 'cooking' } },
            { sa: 'पाचकः', meaning: { hi: 'रसोइया', en: 'cook' } },
            { sa: 'पक्वम्', meaning: { hi: 'पका हुआ', en: 'cooked, ripe' } }
        ],
        examples: [
            { sa: 'माता ओदनं पचति।', hi: 'माता चावल पकाती है।', en: 'Mother cooks rice.' },
            { sa: 'देवदत्तः ओदनं पचते।', hi: 'देवदत्त (अपने लिए) चावल पकाता है।', en: 'Devadatta cooks rice (for himself).' },
            { sa: 'श्वः अहं भोजनं पक्ष्यामि।', hi: 'कल मैं भोजन पकाऊँगा।', en: 'Tomorrow I will cook food.' }
        ],
        notes: [
            {
                hi: 'उभयपदी: जब फल दूसरे के लिए हो तो परस्मैपद (पचति), जब कर्ता स्वयं फल पाए तो आत्मनेपद (पचते)। (स्वरितञितः कर्त्रभिप्राये क्रियाफले 1.3.72)',
                en: 'Ubhayapada: परस्मैपद (पचति) when the result goes to another, आत्मनेपद (पचते) when the doer cooks for himself (स्वरितञितः कर्त्रभिप्राये क्रियाफले 1.3.72).'
            }
        ],
        status: 'verified',
        sources: CORE_SOURCES
    },
    {
        id: 'vad',
        root: 'वद्',
        iast: 'vad',
        meaning: { hi: 'बोलना', en: 'to speak, to say' },
        citation: 'वदति',
        dhatupatha: 'वद व्यक्तायां वाचि',
        gana: 1,
        padaClass: 'parasmaipada',
        it: 'set',
        core: true,
        present: { P: ['वद'] },
        future: ['वदिष्य'],
        lut: ['वदित'],
        presentDerivation: bhvadiNote('वद् + अ'),
        futureDerivation: setFuture('वद् + इ + स्य'),
        related: [
            { sa: 'वादः', meaning: { hi: 'वाद, चर्चा', en: 'discussion, thesis' } },
            { sa: 'संवादः', meaning: { hi: 'बातचीत', en: 'dialogue' } },
            { sa: 'वदनम्', meaning: { hi: 'मुख', en: 'face, mouth' } }
        ],
        examples: [
            { sa: 'शिक्षकः सत्यं वदति।', hi: 'शिक्षक सच बोलते हैं।', en: 'The teacher speaks the truth.' },
            { sa: 'सत्यं वद।', hi: 'सच बोलो।', en: 'Speak the truth.' },
            { sa: 'वयं संस्कृतेन वदामः।', hi: 'हम संस्कृत में बोलते हैं।', en: 'We speak in Sanskrit.' }
        ],
        notes: [
            {
                hi: 'लिट् में सम्प्रसारण होता है (उवाद, ऊदतुः), इसलिए उन्नत स्तर पर अलग से सिखाया जाता है।',
                en: 'Its perfect shows samprasāraṇa (उवाद, ऊदतुः), taught separately at a higher level.'
            }
        ],
        status: 'verified',
        sources: CORE_SOURCES
    },
    {
        id: 'nam',
        root: 'नम्',
        iast: 'nam',
        meaning: { hi: 'झुकना, नमस्कार करना', en: 'to bow, to bend' },
        citation: 'नमति',
        dhatupatha: 'णम प्रह्वत्वे शब्दे च',
        gana: 1,
        padaClass: 'parasmaipada',
        it: 'anit',
        core: true,
        present: { P: ['नम'] },
        future: ['नंस्य'],
        lut: ['नन्त'],
        presentDerivation: bhvadiNote('नम् + अ'),
        futureDerivation: anitFuture(
            'नम् + स्य',
            'म् का अनुस्वार हो जाता है (नश्चापदान्तस्य झलि 8.3.24): नंस्यति।',
            'म् becomes anusvāra before स् (नश्चापदान्तस्य झलि 8.3.24): नंस्यति.'
        ),
        related: [
            { sa: 'नमः', meaning: { hi: 'नमस्कार', en: 'salutation' } },
            { sa: 'प्रणामः', meaning: { hi: 'प्रणाम', en: 'respectful bow' } },
            { sa: 'नम्रः', meaning: { hi: 'विनम्र', en: 'humble' } }
        ],
        examples: [
            { sa: 'शिष्यः गुरुं नमति।', hi: 'शिष्य गुरु को नमन करता है।', en: 'The disciple bows to the teacher.' },
            { sa: 'वृक्षस्य शाखाः नमन्ति।', hi: 'पेड़ की डालियाँ झुकती हैं।', en: 'The branches of the tree bend.' },
            { sa: 'वयं देवं नमामः।', hi: 'हम देव को नमन करते हैं।', en: 'We bow to the deity.' }
        ],
        notes: [
            {
                hi: 'धातुपाठ में "णम" लिखा है (णोपदेश), इसलिए प्र के बाद न का ण होता है: प्रणमति।',
                en: 'The Dhātupāṭha lists it as णम, so after प्र the न becomes ण: प्रणमति.'
            }
        ],
        status: 'verified',
        sources: CORE_SOURCES
    },
    {
        id: 'krid',
        root: 'क्रीड्',
        iast: 'krīḍ',
        meaning: { hi: 'खेलना', en: 'to play' },
        citation: 'क्रीडति',
        dhatupatha: 'क्रीडृ विहारे',
        gana: 1,
        padaClass: 'parasmaipada',
        it: 'set',
        core: true,
        present: { P: ['क्रीड'] },
        future: ['क्रीडिष्य'],
        lut: ['क्रीडित'],
        presentDerivation: bhvadiNote('क्रीड् + अ'),
        futureDerivation: setFuture('क्रीड् + इ + स्य'),
        related: [
            { sa: 'क्रीडा', meaning: { hi: 'खेल', en: 'game, sport' } },
            { sa: 'क्रीडनकम्', meaning: { hi: 'खिलौना', en: 'toy' } },
            { sa: 'क्रीडाङ्गणम्', meaning: { hi: 'खेल का मैदान', en: 'playground' } }
        ],
        examples: [
            { sa: 'बालकाः उद्याने क्रीडन्ति।', hi: 'बालक बगीचे में खेलते हैं।', en: 'The boys play in the garden.' },
            { sa: 'आवां कन्दुकेन क्रीडावः।', hi: 'हम दोनों गेंद से खेलते हैं।', en: 'We two play with a ball.' },
            { sa: 'श्वः वयं क्रीडिष्यामः।', hi: 'कल हम खेलेंगे।', en: 'Tomorrow we will play.' }
        ],
        notes: [],
        status: 'verified',
        sources: CORE_SOURCES
    },
    {
        id: 'kr',
        root: 'कृ',
        iast: 'kṛ',
        meaning: { hi: 'करना', en: 'to do, to make' },
        citation: 'करोति',
        dhatupatha: 'डुकृञ् करणे',
        gana: 8,
        padaClass: 'ubhayapada',
        it: 'anit',
        core: true,
        future: ['करिष्य'],
        lut: ['कर्त'],
        presentDerivation: {
            expr: 'कृ + उ → करो / कुरु / कुर्',
            note: {
                hi: 'तनादिगण: विकरण उ जुड़ता है। सबल रूपों (एकवचन परस्मैपद) में करो-, निर्बल में कुरु-, और म्/व्/य् से पहले कुर्- बनता है।',
                en: 'Class 8 (tanādi): the marker उ is added. Strong forms use करो-, weak forms कुरु-, and before म्, व् or य् the stem is कुर्-.'
            }
        },
        futureDerivation: {
            expr: 'कृ + इ + स्य → कर् + इष्य',
            note: {
                hi: 'ऋ का गुण अर् होता है, और ऋकारान्त धातु से स्य में इट् आता है (ऋद्धनोः स्ये 7.2.70): करिष्यति।',
                en: 'ऋ takes guṇa अर्, and roots ending in ऋ take इ before स्य (ऋद्धनोः स्ये 7.2.70): करिष्यति.'
            }
        },
        explicit: {
            lat: {
                P: tbl('करोति', 'कुरुतः', 'कुर्वन्ति', 'करोषि', 'कुरुथः', 'कुरुथ', 'करोमि', 'कुर्वः', 'कुर्मः'),
                A: tbl('कुरुते', 'कुर्वाते', 'कुर्वते', 'कुरुषे', 'कुर्वाथे', 'कुरुध्वे', 'कुर्वे', 'कुर्वहे', 'कुर्महे')
            },
            lan: {
                P: tbl('अकरोत्', 'अकुरुताम्', 'अकुर्वन्', 'अकरोः', 'अकुरुतम्', 'अकुरुत', 'अकरवम्', 'अकुर्व', 'अकुर्म'),
                A: tbl('अकुरुत', 'अकुर्वाताम्', 'अकुर्वत', 'अकुरुथाः', 'अकुर्वाथाम्', 'अकुरुध्वम्', 'अकुर्वि', 'अकुर्वहि', 'अकुर्महि')
            },
            lot: {
                P: tbl('करोतु/कुरुतात्', 'कुरुताम्', 'कुर्वन्तु', 'कुरु/कुरुतात्', 'कुरुतम्', 'कुरुत', 'करवाणि', 'करवाव', 'करवाम'),
                A: tbl('कुरुताम्', 'कुर्वाताम्', 'कुर्वताम्', 'कुरुष्व', 'कुर्वाथाम्', 'कुरुध्वम्', 'करवै', 'करवावहै', 'करवामहै')
            },
            vidhilin: {
                P: tbl('कुर्यात्', 'कुर्याताम्', 'कुर्युः', 'कुर्याः', 'कुर्यातम्', 'कुर्यात', 'कुर्याम्', 'कुर्याव', 'कुर्याम'),
                A: tbl('कुर्वीत', 'कुर्वीयाताम्', 'कुर्वीरन्', 'कुर्वीथाः', 'कुर्वीयाथाम्', 'कुर्वीध्वम्', 'कुर्वीय', 'कुर्वीवहि', 'कुर्वीमहि')
            },
            lit: {
                P: tbl('चकार', 'चक्रतुः', 'चक्रुः', 'चकर्थ', 'चक्रथुः', 'चक्र', 'चकार/चकर', 'चकृव', 'चकृम'),
                A: tbl('चक्रे', 'चक्राते', 'चक्रिरे', 'चकृषे', 'चक्राथे', 'चकृढ्वे', 'चक्रे', 'चकृवहे', 'चकृमहे')
            },
            lun: {
                P: tbl('अकार्षीत्', 'अकार्ष्टाम्', 'अकार्षुः', 'अकार्षीः', 'अकार्ष्टम्', 'अकार्ष्ट', 'अकार्षम्', 'अकार्ष्व', 'अकार्ष्म'),
                A: tbl('अकृत', 'अकृषाताम्', 'अकृषत', 'अकृथाः', 'अकृषाथाम्', 'अकृढ्वम्', 'अकृषि', 'अकृष्वहि', 'अकृष्महि')
            },
            ashirlin: {
                P: tbl('क्रियात्', 'क्रियास्ताम्', 'क्रियासुः', 'क्रियाः', 'क्रियास्तम्', 'क्रियास्त', 'क्रियासम्', 'क्रियास्व', 'क्रियास्म'),
                A: tbl('कृषीष्ट', 'कृषीयास्ताम्', 'कृषीरन्', 'कृषीष्ठाः', 'कृषीयास्थाम्', 'कृषीढ्वम्', 'कृषीय', 'कृषीवहि', 'कृषीमहि')
            }
        },
        explicitStatus: {
            lat: 'verified',
            lan: 'verified',
            lot: 'verified',
            vidhilin: 'verified',
            lit: 'needs-review',
            lun: 'needs-review',
            ashirlin: 'needs-review'
        },
        related: [
            { sa: 'कार्यम्', meaning: { hi: 'काम', en: 'work, task' } },
            { sa: 'कर्ता', meaning: { hi: 'करने वाला', en: 'doer' } },
            { sa: 'कर्म', meaning: { hi: 'कर्म', en: 'action' } },
            { sa: 'कृतिः', meaning: { hi: 'रचना', en: 'creation' } }
        ],
        examples: [
            { sa: 'सः कार्यं करोति।', hi: 'वह काम करता है।', en: 'He does the work.' },
            { sa: 'अहं प्रणामं करोमि।', hi: 'मैं प्रणाम करता हूँ।', en: 'I offer my salutation.' },
            { sa: 'यूयं किं कुरुथ?', hi: 'तुम सब क्या कर रहे हो?', en: 'What are you all doing?' }
        ],
        notes: [
            {
                hi: 'विशेष धातु: करो-, कुरु-, कुर्- तीन अङ्ग। इसके वर्तमान रूप याद किए जाते हैं।',
                en: 'An irregular root with three present stems (करो-, कुरु-, कुर्-). Its present forms are memorised.'
            },
            {
                hi: 'आत्मनेपद के लिट्, लुङ्, आशीर्लिङ् रूप विद्वान्-समीक्षा के लिए चिह्नित हैं।',
                en: 'The ātmanepada perfect, aorist and benedictive forms are flagged for scholarly review.'
            }
        ],
        status: 'verified',
        sources: CORE_SOURCES
    },
    {
        id: 'drs',
        root: 'दृश्',
        iast: 'dṛś',
        meaning: { hi: 'देखना', en: 'to see' },
        citation: 'पश्यति',
        dhatupatha: 'दृशिर् प्रेक्षणे',
        gana: 1,
        padaClass: 'parasmaipada',
        it: 'anit',
        core: true,
        present: { P: ['पश्य'] },
        future: ['द्रक्ष्य'],
        lut: ['द्रष्ट'],
        presentDerivation: {
            expr: 'दृश् → पश्य',
            note: {
                hi: 'विशेष अङ्ग: वर्तमान-वर्ग के लकारों में दृश् के स्थान पर पश्य आता है (पाघ्राध्मा... 7.3.78)।',
                en: 'Special stem: in the present-system lakāras दृश् is replaced by पश्य (पाघ्राध्मा… 7.3.78).'
            }
        },
        futureDerivation: anitFuture(
            'दृश् + स्य → द्रश् + स्य → द्रक्ष्य',
            'अम् आगम से दृश् का द्रश् होता है (सृजिदृशोर्झल्यमकिति 6.1.58), फिर श् → ष् → क् और स् → ष्।',
            'The augment अम् turns दृश् into द्रश् (सृजिदृशोर्झल्यमकिति 6.1.58); then श् → ष् → क् and स् → ष्.'
        ),
        explicit: {
            lit: { P: tbl('ददर्श', 'ददृशतुः', 'ददृशुः', 'ददर्शिथ/दद्रष्ठ', 'ददृशथुः', 'ददृश', 'ददर्श', 'ददृशिव', 'ददृशिम') },
            lun: { P: tbl('अदर्शत्/अद्राक्षीत्', 'अदर्शताम्/अद्राष्टाम्', 'अदर्शन्/अद्राक्षुः', 'अदर्शः/अद्राक्षीः', 'अदर्शतम्/अद्राष्टम्', 'अदर्शत/अद्राष्ट', 'अदर्शम्/अद्राक्षम्', 'अदर्शाव/अद्राक्ष्व', 'अदर्शाम/अद्राक्ष्म') },
            ashirlin: { P: tbl('दृश्यात्', 'दृश्यास्ताम्', 'दृश्यासुः', 'दृश्याः', 'दृश्यास्तम्', 'दृश्यास्त', 'दृश्यासम्', 'दृश्यास्व', 'दृश्यास्म') }
        },
        explicitStatus: { lit: 'verified', lun: 'needs-review', ashirlin: 'verified' },
        related: [
            { sa: 'दर्शनम्', meaning: { hi: 'दर्शन, देखना', en: 'sight, philosophy' } },
            { sa: 'दृष्टिः', meaning: { hi: 'दृष्टि', en: 'vision' } },
            { sa: 'दृश्यम्', meaning: { hi: 'दृश्य', en: 'scene' } }
        ],
        examples: [
            { sa: 'अहं चन्द्रं पश्यामि।', hi: 'मैं चाँद देखता हूँ।', en: 'I see the moon.' },
            { sa: 'बालकाः आकाशे खगान् पश्यन्ति।', hi: 'बालक आकाश में पक्षियों को देखते हैं।', en: 'The boys see birds in the sky.' },
            { sa: 'त्वं किं पश्यसि?', hi: 'तुम क्या देखते हो?', en: 'What do you see?' }
        ],
        notes: [
            {
                hi: 'वर्तमान रूप पश्य से (पश्यति), बाकी दृश् से (द्रक्ष्यति, ददर्श)। एक ही धातु, दो रूप-आधार।',
                en: 'Present forms come from पश्य (पश्यति), the rest from दृश् (द्रक्ष्यति, ददर्श): one root, two bases.'
            },
            {
                hi: 'लुङ् के दो प्रकार (अदर्शत् / अद्राक्षीत्) विद्वान्-समीक्षा हेतु चिह्नित हैं।',
                en: 'Its two aorist types (अदर्शत् / अद्राक्षीत्) are flagged for scholarly review.'
            }
        ],
        status: 'verified',
        sources: CORE_SOURCES
    },
    {
        id: 'pa',
        root: 'पा',
        iast: 'pā',
        meaning: { hi: 'पीना', en: 'to drink' },
        citation: 'पिबति',
        dhatupatha: 'पा पाने',
        gana: 1,
        padaClass: 'parasmaipada',
        it: 'anit',
        core: true,
        present: { P: ['पिब'] },
        future: ['पास्य'],
        lut: ['पात'],
        presentDerivation: {
            expr: 'पा → पिब',
            note: {
                hi: 'विशेष अङ्ग: वर्तमान-वर्ग के लकारों में पा के स्थान पर पिब आता है (पाघ्राध्मा... 7.3.78)।',
                en: 'Special stem: in the present-system lakāras पा is replaced by पिब (पाघ्राध्मा… 7.3.78).'
            }
        },
        futureDerivation: anitFuture('पा + स्य', 'पास्यति।', 'पास्यति.'),
        explicit: {
            lit: { P: tbl('पपौ', 'पपतुः', 'पपुः', 'पपिथ/पपाथ', 'पपथुः', 'पप', 'पपौ', 'पपिव', 'पपिम') },
            lun: { P: tbl('अपात्', 'अपाताम्', 'अपुः', 'अपाः', 'अपातम्', 'अपात', 'अपाम्', 'अपाव', 'अपाम') },
            ashirlin: { P: tbl('पेयात्', 'पेयास्ताम्', 'पेयासुः', 'पेयाः', 'पेयास्तम्', 'पेयास्त', 'पेयासम्', 'पेयास्व', 'पेयास्म') }
        },
        explicitStatus: { lit: 'verified', lun: 'verified', ashirlin: 'verified' },
        related: [
            { sa: 'पानम्', meaning: { hi: 'पीना', en: 'drinking' } },
            { sa: 'पेयम्', meaning: { hi: 'पेय', en: 'drink' } },
            { sa: 'पिपासा', meaning: { hi: 'प्यास', en: 'thirst' } }
        ],
        examples: [
            { sa: 'बालकः दुग्धं पिबति।', hi: 'बालक दूध पीता है।', en: 'The boy drinks milk.' },
            { sa: 'वयं जलं पिबामः।', hi: 'हम पानी पीते हैं।', en: 'We drink water.' },
            { sa: 'धेनवः जलं पिबन्ति।', hi: 'गायें पानी पीती हैं।', en: 'The cows drink water.' }
        ],
        notes: [
            {
                hi: 'वर्तमान में पिब (पिबति), पर भविष्यत् में मूल पा (पास्यति)।',
                en: 'Present on पिब (पिबति), but the future uses the bare root पा (पास्यति).'
            }
        ],
        status: 'verified',
        sources: CORE_SOURCES
    },
    {
        id: 'stha',
        root: 'स्था',
        iast: 'sthā',
        meaning: { hi: 'ठहरना, खड़ा होना', en: 'to stand, to stay' },
        citation: 'तिष्ठति',
        dhatupatha: 'ष्ठा गतिनिवृत्तौ',
        gana: 1,
        padaClass: 'parasmaipada',
        it: 'anit',
        core: true,
        present: { P: ['तिष्ठ'] },
        future: ['स्थास्य'],
        lut: ['स्थात'],
        presentDerivation: {
            expr: 'स्था → तिष्ठ',
            note: {
                hi: 'विशेष अङ्ग: वर्तमान-वर्ग के लकारों में स्था के स्थान पर तिष्ठ आता है (पाघ्राध्मा... 7.3.78)।',
                en: 'Special stem: in the present-system lakāras स्था is replaced by तिष्ठ (पाघ्राध्मा… 7.3.78).'
            }
        },
        futureDerivation: anitFuture('स्था + स्य', 'स्थास्यति।', 'स्थास्यति.'),
        explicit: {
            lit: { P: tbl('तस्थौ', 'तस्थतुः', 'तस्थुः', 'तस्थिथ/तस्थाथ', 'तस्थथुः', 'तस्थ', 'तस्थौ', 'तस्थिव', 'तस्थिम') },
            lun: { P: tbl('अस्थात्', 'अस्थाताम्', 'अस्थुः', 'अस्थाः', 'अस्थातम्', 'अस्थात', 'अस्थाम्', 'अस्थाव', 'अस्थाम') },
            ashirlin: { P: tbl('स्थेयात्', 'स्थेयास्ताम्', 'स्थेयासुः', 'स्थेयाः', 'स्थेयास्तम्', 'स्थेयास्त', 'स्थेयासम्', 'स्थेयास्व', 'स्थेयास्म') }
        },
        explicitStatus: { lit: 'verified', lun: 'verified', ashirlin: 'verified' },
        related: [
            { sa: 'स्थानम्', meaning: { hi: 'स्थान', en: 'place' } },
            { sa: 'स्थितिः', meaning: { hi: 'स्थिति', en: 'state, position' } },
            { sa: 'स्थिरः', meaning: { hi: 'स्थिर', en: 'steady' } }
        ],
        examples: [
            { sa: 'छात्राः कक्षायां तिष्ठन्ति।', hi: 'छात्र कक्षा में खड़े हैं।', en: 'The students stand in the classroom.' },
            { sa: 'त्वम् अत्र तिष्ठ।', hi: 'तुम यहाँ ठहरो।', en: 'Stay here.' },
            { sa: 'वृक्षः मार्गस्य पार्श्वे तिष्ठति।', hi: 'पेड़ रास्ते के किनारे खड़ा है।', en: 'The tree stands beside the road.' }
        ],
        notes: [
            {
                hi: 'परस्मैपदी, पर सम्, अव, प्र, वि के बाद आत्मनेपद होता है (समवप्रविभ्यः स्थः 1.3.22): प्रतिष्ठते, अवतिष्ठते।',
                en: 'Parasmaipada, but after सम्, अव, प्र, वि it takes ātmanepada (समवप्रविभ्यः स्थः 1.3.22): प्रतिष्ठते, अवतिष्ठते.'
            }
        ],
        status: 'verified',
        sources: CORE_SOURCES
    }
]

interface SupportSpec {
    id: string
    root: string
    iast: string
    meaning: Bi
    gana: Gana
    padaClass: PadaClass
    present: string
    pada?: Pada
    future: string[]
    imperfect?: string
    conditional?: string[]
    upasarga?: string
    baseRootId?: string
    presentExpr?: string
    example: DhatuExample
    note?: Bi
}

const supporting = (spec: SupportSpec): Dhatu => {
    const pada: Pada = spec.pada ?? (spec.padaClass === 'atmanepada' ? 'A' : 'P')

    return {
        id: spec.id,
        root: spec.root,
        iast: spec.iast,
        meaning: spec.meaning,
        citation: spec.present + (pada === 'A' ? 'ते' : 'ति'),
        gana: spec.gana,
        padaClass: spec.padaClass,
        core: false,
        upasarga: spec.upasarga,
        baseRootId: spec.baseRootId,
        present: { [pada]: [spec.present] },
        imperfect: spec.imperfect ? { [pada]: [spec.imperfect] } : undefined,
        future: spec.future,
        conditional: spec.conditional,
        presentDerivation: spec.upasarga
            ? {
                expr: spec.presentExpr ?? `${spec.upasarga} + ${spec.present.slice(spec.upasarga.length) || spec.present}`,
                note: {
                    hi: `उपसर्ग ${spec.upasarga} धातु से पहले जुड़ता है। लङ् में अ (अट्) उपसर्ग और धातु के बीच आता है।`,
                    en: `The prefix ${spec.upasarga} comes before the verb. In the past (लङ्), the augment अ goes between prefix and verb.`
                }
            }
            : undefined,
        related: [],
        examples: [spec.example],
        notes: spec.note ? [spec.note] : [],
        status: 'verified',
        sources: ['dhatupatha', 'kale', 'apte-dict']
    }
}

export const SUPPORTING_DHATUS: Dhatu[] = [
    supporting({ id: 'pat', root: 'पत्', iast: 'pat', meaning: { hi: 'गिरना', en: 'to fall' }, gana: 1, padaClass: 'parasmaipada', present: 'पत', future: ['पतिष्य'], example: { sa: 'वृक्षात् पत्रं पतति।', hi: 'पेड़ से पत्ता गिरता है।', en: 'A leaf falls from the tree.' } }),
    supporting({ id: 'dhav', root: 'धाव्', iast: 'dhāv', meaning: { hi: 'दौड़ना', en: 'to run' }, gana: 1, padaClass: 'parasmaipada', present: 'धाव', future: ['धाविष्य'], example: { sa: 'अश्वः वेगेन धावति।', hi: 'घोड़ा तेज़ी से दौड़ता है।', en: 'The horse runs fast.' } }),
    supporting({ id: 'vas', root: 'वस्', iast: 'vas', meaning: { hi: 'रहना', en: 'to live, to dwell' }, gana: 1, padaClass: 'parasmaipada', present: 'वस', future: ['वत्स्य'], example: { sa: 'वयं नगरे वसामः।', hi: 'हम नगर में रहते हैं।', en: 'We live in the city.' } }),
    supporting({ id: 'bhram', root: 'भ्रम्', iast: 'bhram', meaning: { hi: 'घूमना', en: 'to wander' }, gana: 1, padaClass: 'parasmaipada', present: 'भ्रम', future: ['भ्रमिष्य'], example: { sa: 'भ्रमरः उद्याने भ्रमति।', hi: 'भौंरा बगीचे में घूमता है।', en: 'The bee wanders in the garden.' }, note: { hi: 'दिवादिगण में भी: भ्राम्यति।', en: 'Also class 4: भ्राम्यति.' } }),
    supporting({ id: 'ksip', root: 'क्षिप्', iast: 'kṣip', meaning: { hi: 'फेंकना', en: 'to throw' }, gana: 6, padaClass: 'parasmaipada', present: 'क्षिप', future: ['क्षेप्स्य'], example: { sa: 'बालकः कन्दुकं क्षिपति।', hi: 'बालक गेंद फेंकता है।', en: 'The boy throws the ball.' } }),
    supporting({ id: 'sidh', root: 'सिध्', iast: 'sidh', meaning: { hi: 'सिद्ध होना, सफल होना', en: 'to succeed' }, gana: 4, padaClass: 'parasmaipada', present: 'सिध्य', future: ['सेत्स्य'], example: { sa: 'परिश्रमेण कार्यं सिध्यति।', hi: 'परिश्रम से काम सिद्ध होता है।', en: 'Work succeeds through effort.' } }),
    supporting({ id: 'kuj', root: 'कूज्', iast: 'kūj', meaning: { hi: 'कूकना, चहचहाना', en: 'to coo, to warble' }, gana: 1, padaClass: 'parasmaipada', present: 'कूज', future: ['कूजिष्य'], example: { sa: 'कोकिलः मधुरं कूजति।', hi: 'कोयल मीठा कूकती है।', en: 'The cuckoo sings sweetly.' } }),
    supporting({ id: 'nrt', root: 'नृत्', iast: 'nṛt', meaning: { hi: 'नाचना', en: 'to dance' }, gana: 4, padaClass: 'parasmaipada', present: 'नृत्य', future: ['नर्तिष्य', 'नर्त्स्य'], example: { sa: 'मयूरः वने नृत्यति।', hi: 'मोर वन में नाचता है।', en: 'The peacock dances in the forest.' }, note: { hi: 'भविष्यत् में दो स्वीकृत रूप: नर्तिष्यति और नर्त्स्यति (7.2.57)।', en: 'Two accepted futures: नर्तिष्यति and नर्त्स्यति (7.2.57).' } }),
    supporting({ id: 'mil', root: 'मिल्', iast: 'mil', meaning: { hi: 'मिलना', en: 'to meet' }, gana: 6, padaClass: 'parasmaipada', present: 'मिल', future: ['मेलिष्य'], example: { sa: 'मित्राणि उद्याने मिलन्ति।', hi: 'मित्र बगीचे में मिलते हैं।', en: 'Friends meet in the garden.' } }),
    supporting({ id: 'cal', root: 'चल्', iast: 'cal', meaning: { hi: 'चलना', en: 'to move' }, gana: 1, padaClass: 'parasmaipada', present: 'चल', future: ['चलिष्य'], example: { sa: 'नौका जले चलति।', hi: 'नाव पानी में चलती है।', en: 'The boat moves on the water.' } }),
    supporting({ id: 'vis', root: 'विश्', iast: 'viś', meaning: { hi: 'प्रवेश करना', en: 'to enter' }, gana: 6, padaClass: 'parasmaipada', present: 'विश', future: ['वेक्ष्य'], example: { sa: 'सिंहः गुहां विशति।', hi: 'शेर गुफा में प्रवेश करता है।', en: 'The lion enters the cave.' } }),
    supporting({ id: 'smr', root: 'स्मृ', iast: 'smṛ', meaning: { hi: 'याद करना', en: 'to remember' }, gana: 1, padaClass: 'parasmaipada', present: 'स्मर', future: ['स्मरिष्य'], example: { sa: 'अहं मातरं स्मरामि।', hi: 'मैं माँ को याद करता हूँ।', en: 'I remember my mother.' } }),
    supporting({ id: 'gai', root: 'गै', iast: 'gai', meaning: { hi: 'गाना', en: 'to sing' }, gana: 1, padaClass: 'parasmaipada', present: 'गाय', future: ['गास्य'], example: { sa: 'बालिका गीतं गायति।', hi: 'बालिका गीत गाती है।', en: 'The girl sings a song.' } }),
    supporting({ id: 'ji', root: 'जि', iast: 'ji', meaning: { hi: 'जीतना', en: 'to win, to conquer' }, gana: 1, padaClass: 'parasmaipada', present: 'जय', future: ['जेष्य'], example: { sa: 'वीरः युद्धं जयति।', hi: 'वीर युद्ध जीतता है।', en: 'The hero wins the battle.' } }),
    supporting({ id: 'ni', root: 'नी', iast: 'nī', meaning: { hi: 'ले जाना', en: 'to lead, to take' }, gana: 1, padaClass: 'ubhayapada', present: 'नय', pada: 'P', future: ['नेष्य'], example: { sa: 'सः बालकं विद्यालयं नयति।', hi: 'वह बालक को विद्यालय ले जाता है।', en: 'He takes the boy to school.' } }),
    supporting({ id: 'hr', root: 'हृ', iast: 'hṛ', meaning: { hi: 'हरना, ले जाना', en: 'to take away, to carry' }, gana: 1, padaClass: 'ubhayapada', present: 'हर', pada: 'P', future: ['हरिष्य'], example: { sa: 'चौरः धनं हरति।', hi: 'चोर धन चुराता है।', en: 'The thief steals the money.' } }),
    supporting({ id: 'raks', root: 'रक्ष्', iast: 'rakṣ', meaning: { hi: 'रक्षा करना', en: 'to protect' }, gana: 1, padaClass: 'parasmaipada', present: 'रक्ष', future: ['रक्षिष्य'], example: { sa: 'सैनिकाः देशं रक्षन्ति।', hi: 'सैनिक देश की रक्षा करते हैं।', en: 'Soldiers protect the country.' } }),
    supporting({ id: 'is', root: 'इष्', iast: 'iṣ', meaning: { hi: 'चाहना', en: 'to wish, to want' }, gana: 6, padaClass: 'parasmaipada', present: 'इच्छ', imperfect: 'ऐच्छ', future: ['एषिष्य'], conditional: ['ऐषिष्य'], example: { sa: 'अहं जलं पातुम् इच्छामि।', hi: 'मैं पानी पीना चाहता हूँ।', en: 'I want to drink water.' }, note: { hi: 'स्वर से आरम्भ होने वाली धातु: लङ् में अ + इ = ऐ (आडजादीनाम्): ऐच्छत्।', en: 'A vowel-initial root: in लङ् the augment combines as ऐ: ऐच्छत्.' } }),
    supporting({ id: 'pracch', root: 'प्रच्छ्', iast: 'pracch', meaning: { hi: 'पूछना', en: 'to ask' }, gana: 6, padaClass: 'parasmaipada', present: 'पृच्छ', future: ['प्रक्ष्य'], example: { sa: 'शिष्यः गुरुं प्रश्नं पृच्छति।', hi: 'शिष्य गुरु से प्रश्न पूछता है।', en: 'The student asks the teacher a question.' } }),
    supporting({ id: 'muc', root: 'मुच्', iast: 'muc', meaning: { hi: 'छोड़ना', en: 'to release' }, gana: 6, padaClass: 'ubhayapada', present: 'मुञ्च', pada: 'P', future: ['मोक्ष्य'], example: { sa: 'व्याधः खगं मुञ्चति।', hi: 'शिकारी पक्षी को छोड़ता है।', en: 'The hunter releases the bird.' } }),
    supporting({ id: 'krt', root: 'कृत्', iast: 'kṛt', meaning: { hi: 'काटना', en: 'to cut' }, gana: 6, padaClass: 'parasmaipada', present: 'कृन्त', future: ['कर्तिष्य', 'कर्त्स्य'], example: { sa: 'मूषकः जालं कृन्तति।', hi: 'चूहा जाल काटता है।', en: 'The mouse cuts the net.' } }),
    supporting({ id: 'labh', root: 'लभ्', iast: 'labh', meaning: { hi: 'पाना', en: 'to obtain, to get' }, gana: 1, padaClass: 'atmanepada', present: 'लभ', future: ['लप्स्य'], example: { sa: 'छात्रः पुरस्कारं लभते।', hi: 'छात्र पुरस्कार पाता है।', en: 'The student receives a prize.' } }),
    supporting({ id: 'sev', root: 'सेव्', iast: 'sev', meaning: { hi: 'सेवा करना', en: 'to serve' }, gana: 1, padaClass: 'atmanepada', present: 'सेव', future: ['सेविष्य'], example: { sa: 'पुत्रः पितरं सेवते।', hi: 'पुत्र पिता की सेवा करता है।', en: 'The son serves his father.' } }),
    supporting({ id: 'ruc', root: 'रुच्', iast: 'ruc', meaning: { hi: 'अच्छा लगना', en: 'to please, to be liked' }, gana: 1, padaClass: 'atmanepada', present: 'रोच', future: ['रोचिष्य'], example: { sa: 'मह्यं मोदकः रोचते।', hi: 'मुझे लड्डू अच्छा लगता है।', en: 'I like laddus (the laddu pleases me).' }, note: { hi: 'जिसे अच्छा लगे वह चतुर्थी में (रुच्यर्थानां प्रीयमाणः 1.4.33)।', en: 'The one who likes it stands in चतुर्थी (रुच्यर्थानां प्रीयमाणः 1.4.33).' } }),
    supporting({ id: 'dhr', root: 'धृ', iast: 'dhṛ', meaning: { hi: 'पकड़ना, धारण करना', en: 'to hold, to bear' }, gana: 1, padaClass: 'ubhayapada', present: 'धर', pada: 'P', future: ['धरिष्य'], example: { sa: 'सिंहः मूषकं पादेन अधरत्।', hi: 'शेर ने चूहे को पंजे से पकड़ लिया।', en: 'The lion held the mouse with its paw.' } }),
    supporting({ id: 'vah', root: 'वह्', iast: 'vah', meaning: { hi: 'बहना, ढोना', en: 'to carry, to flow' }, gana: 1, padaClass: 'ubhayapada', present: 'वह', pada: 'P', future: ['वक्ष्य'], example: { sa: 'नदी वहति।', hi: 'नदी बहती है।', en: 'The river flows.' } }),
    supporting({ id: 'prakas', root: 'प्र + काश्', iast: 'pra-kāś', meaning: { hi: 'चमकना', en: 'to shine' }, gana: 1, padaClass: 'atmanepada', present: 'प्रकाश', imperfect: 'प्राकाश', future: ['प्रकाशिष्य'], conditional: ['प्राकाशिष्य'], upasarga: 'प्र', example: { sa: 'सूर्यः प्रकाशते।', hi: 'सूर्य चमकता है।', en: 'The sun shines.' } }),
    supporting({ id: 'agam', root: 'आ + गम्', iast: 'ā-gam', meaning: { hi: 'आना', en: 'to come' }, gana: 1, padaClass: 'parasmaipada', present: 'आगच्छ', imperfect: 'आगच्छ', future: ['आगमिष्य'], conditional: ['आगमिष्य'], upasarga: 'आ', baseRootId: 'gam', example: { sa: 'मित्रं गृहम् आगच्छति।', hi: 'मित्र घर आता है।', en: 'A friend comes home.' } }),
    supporting({ id: 'utstha', root: 'उत् + स्था', iast: 'ut-sthā', meaning: { hi: 'उठना', en: 'to stand up, to rise' }, gana: 1, padaClass: 'parasmaipada', present: 'उत्तिष्ठ', imperfect: 'उदतिष्ठ', future: ['उत्थास्य'], conditional: ['उदस्थास्य'], upasarga: 'उत्', baseRootId: 'stha', example: { sa: 'सः प्रातः उत्तिष्ठति।', hi: 'वह सुबह उठता है।', en: 'He gets up in the morning.' }, note: { hi: 'उत् + स्था में स् लुप्त हो जाता है (उदः स्थास्तम्भोः पूर्वस्य 8.4.61): उत्तिष्ठति।', en: 'In उत् + स्था the स् is lost (उदः स्थास्तम्भोः पूर्वस्य 8.4.61): उत्तिष्ठति.' } }),
    supporting({ id: 'upavis', root: 'उप + विश्', iast: 'upa-viś', meaning: { hi: 'बैठना', en: 'to sit down' }, gana: 6, padaClass: 'parasmaipada', present: 'उपविश', imperfect: 'उपाविश', future: ['उपवेक्ष्य'], conditional: ['उपावेक्ष्य'], upasarga: 'उप', baseRootId: 'vis', example: { sa: 'छात्राः आसने उपविशन्ति।', hi: 'छात्र आसन पर बैठते हैं।', en: 'The students sit on the seat.' } }),
    supporting({ id: 'anubhu', root: 'अनु + भू', iast: 'anu-bhū', meaning: { hi: 'अनुभव करना', en: 'to experience' }, gana: 1, padaClass: 'parasmaipada', present: 'अनुभव', imperfect: 'अन्वभव', future: ['अनुभविष्य'], conditional: ['अन्वभविष्य'], upasarga: 'अनु', baseRootId: 'bhu', example: { sa: 'सः सुखम् अनुभवति।', hi: 'वह सुख का अनुभव करता है।', en: 'He experiences happiness.' } }),
    supporting({ id: 'pravis', root: 'प्र + विश्', iast: 'pra-viś', meaning: { hi: 'प्रवेश करना', en: 'to enter' }, gana: 6, padaClass: 'parasmaipada', present: 'प्रविश', imperfect: 'प्राविश', future: ['प्रवेक्ष्य'], conditional: ['प्रावेक्ष्य'], upasarga: 'प्र', baseRootId: 'vis', example: { sa: 'शिक्षकः कक्षां प्रविशति।', hi: 'शिक्षक कक्षा में प्रवेश करते हैं।', en: 'The teacher enters the classroom.' } }),
    supporting({ id: 'pranam', root: 'प्र + नम्', iast: 'pra-ṇam', meaning: { hi: 'प्रणाम करना', en: 'to bow, to salute' }, gana: 1, padaClass: 'parasmaipada', present: 'प्रणम', presentExpr: 'प्र + नम (न → ण)', imperfect: 'प्राणम', future: ['प्रणंस्य'], conditional: ['प्राणंस्य'], upasarga: 'प्र', baseRootId: 'nam', example: { sa: 'अहं गुरुं प्रणमामि।', hi: 'मैं गुरु को प्रणाम करता हूँ।', en: 'I bow to my teacher.' } }),
    supporting({ id: 'sangam', root: 'सम् + गम्', iast: 'sam-gam', meaning: { hi: 'मिलना, साथ जाना', en: 'to meet, to come together' }, gana: 1, padaClass: 'atmanepada', present: 'सङ्गच्छ', imperfect: 'समगच्छ', future: ['सङ्गंस्य'], conditional: ['समगंस्य'], upasarga: 'सम्', baseRootId: 'gam', example: { sa: 'मित्राणि उद्याने सङ्गच्छन्ते।', hi: 'मित्र बगीचे में मिलते हैं।', en: 'The friends meet in the garden.' }, note: { hi: 'सम् के बाद गम् आत्मनेपदी होता है (समो गम्यृच्छिभ्याम् 1.3.29)।', en: 'After सम्, गम् takes ātmanepada (समो गम्यृच्छिभ्याम् 1.3.29).' } }),
    supporting({ id: 'avagam', root: 'अव + गम्', iast: 'ava-gam', meaning: { hi: 'समझना', en: 'to understand' }, gana: 1, padaClass: 'parasmaipada', present: 'अवगच्छ', imperfect: 'अवागच्छ', future: ['अवगमिष्य'], conditional: ['अवागमिष्य'], upasarga: 'अव', baseRootId: 'gam', example: { sa: 'छात्रः पाठम् अवगच्छति।', hi: 'छात्र पाठ समझता है।', en: 'The student understands the lesson.' } }),
    supporting({ id: 'adhigam', root: 'अधि + गम्', iast: 'adhi-gam', meaning: { hi: 'प्राप्त करना', en: 'to attain' }, gana: 1, padaClass: 'parasmaipada', present: 'अधिगच्छ', imperfect: 'अध्यगच्छ', future: ['अधिगमिष्य'], conditional: ['अध्यगमिष्य'], upasarga: 'अधि', baseRootId: 'gam', example: { sa: 'सः ज्ञानम् अधिगच्छति।', hi: 'वह ज्ञान प्राप्त करता है।', en: 'He attains knowledge.' } }),
    supporting({ id: 'nirgam', root: 'निर् + गम्', iast: 'nir-gam', meaning: { hi: 'बाहर जाना', en: 'to go out' }, gana: 1, padaClass: 'parasmaipada', present: 'निर्गच्छ', imperfect: 'निरगच्छ', future: ['निर्गमिष्य'], conditional: ['निरगमिष्य'], upasarga: 'निर्', baseRootId: 'gam', example: { sa: 'बालकः गृहात् निर्गच्छति।', hi: 'बालक घर से बाहर जाता है।', en: 'The boy goes out of the house.' } }),
    supporting({ id: 'pratigam', root: 'प्रति + गम्', iast: 'prati-gam', meaning: { hi: 'लौटना', en: 'to go back, to return' }, gana: 1, padaClass: 'parasmaipada', present: 'प्रतिगच्छ', imperfect: 'प्रत्यगच्छ', future: ['प्रतिगमिष्य'], conditional: ['प्रत्यगमिष्य'], upasarga: 'प्रति', baseRootId: 'gam', example: { sa: 'सायं पिता गृहं प्रतिगच्छति।', hi: 'शाम को पिता घर लौटते हैं।', en: 'In the evening father returns home.' } }),
    supporting({ id: 'udgam', root: 'उत् + गम्', iast: 'ud-gam', meaning: { hi: 'ऊपर जाना, उदय होना', en: 'to rise' }, gana: 1, padaClass: 'parasmaipada', present: 'उद्गच्छ', imperfect: 'उदगच्छ', future: ['उद्गमिष्य'], conditional: ['उदगमिष्य'], upasarga: 'उत्', baseRootId: 'gam', example: { sa: 'सूर्यः पूर्वस्यां दिशि उद्गच्छति।', hi: 'सूर्य पूर्व दिशा में उगता है।', en: 'The sun rises in the east.' } }),
    supporting({ id: 'anugam', root: 'अनु + गम्', iast: 'anu-gam', meaning: { hi: 'पीछे चलना', en: 'to follow' }, gana: 1, padaClass: 'parasmaipada', present: 'अनुगच्छ', imperfect: 'अन्वगच्छ', future: ['अनुगमिष्य'], conditional: ['अन्वगमिष्य'], upasarga: 'अनु', baseRootId: 'gam', example: { sa: 'शिष्याः गुरुम् अनुगच्छन्ति।', hi: 'शिष्य गुरु के पीछे चलते हैं।', en: 'The disciples follow the teacher.' } }),
    supporting({ id: 'ahr', root: 'आ + हृ', iast: 'ā-hṛ', meaning: { hi: 'लाना', en: 'to bring' }, gana: 1, padaClass: 'ubhayapada', present: 'आहर', pada: 'P', imperfect: 'आहर', future: ['आहरिष्य'], conditional: ['आहरिष्य'], upasarga: 'आ', baseRootId: 'hr', example: { sa: 'बालकः जलम् आहरति।', hi: 'बालक पानी लाता है।', en: 'The boy brings water.' } }),
    supporting({ id: 'prahr', root: 'प्र + हृ', iast: 'pra-hṛ', meaning: { hi: 'प्रहार करना', en: 'to strike' }, gana: 1, padaClass: 'ubhayapada', present: 'प्रहर', pada: 'P', imperfect: 'प्राहर', future: ['प्रहरिष्य'], conditional: ['प्राहरिष्य'], upasarga: 'प्र', baseRootId: 'hr', example: { sa: 'सैनिकः शत्रुं प्रहरति।', hi: 'सैनिक शत्रु पर प्रहार करता है।', en: 'The soldier strikes the enemy.' } }),
    supporting({ id: 'samhr', root: 'सम् + हृ', iast: 'sam-hṛ', meaning: { hi: 'संहार करना, समेटना', en: 'to withdraw, to destroy' }, gana: 1, padaClass: 'ubhayapada', present: 'संहर', presentExpr: 'सम् + हर', pada: 'P', imperfect: 'समहर', future: ['संहरिष्य'], conditional: ['समहरिष्य'], upasarga: 'सम्', baseRootId: 'hr', example: { sa: 'शिवः जगत् संहरति।', hi: 'शिव जगत् का संहार करते हैं।', en: 'Śiva withdraws the world.' } }),
    supporting({ id: 'vihr', root: 'वि + हृ', iast: 'vi-hṛ', meaning: { hi: 'विहार करना, घूमना', en: 'to roam for pleasure' }, gana: 1, padaClass: 'ubhayapada', present: 'विहर', pada: 'P', imperfect: 'व्यहर', future: ['विहरिष्य'], conditional: ['व्यहरिष्य'], upasarga: 'वि', baseRootId: 'hr', example: { sa: 'बालकाः उद्याने विहरन्ति।', hi: 'बालक बगीचे में घूमते हैं।', en: 'The boys stroll in the garden.' } }),
    supporting({ id: 'parihr', root: 'परि + हृ', iast: 'pari-hṛ', meaning: { hi: 'त्यागना, बचना', en: 'to avoid' }, gana: 1, padaClass: 'ubhayapada', present: 'परिहर', pada: 'P', imperfect: 'पर्यहर', future: ['परिहरिष्य'], conditional: ['पर्यहरिष्य'], upasarga: 'परि', baseRootId: 'hr', example: { sa: 'सः दोषान् परिहरति।', hi: 'वह दोषों से बचता है।', en: 'He avoids faults.' } }),
    supporting({ id: 'upadis', root: 'उप + दिश्', iast: 'upa-diś', meaning: { hi: 'उपदेश देना', en: 'to teach, to advise' }, gana: 6, padaClass: 'parasmaipada', present: 'उपदिश', imperfect: 'उपादिश', future: ['उपदेक्ष्य'], conditional: ['उपादेक्ष्य'], upasarga: 'उप', example: { sa: 'गुरुः शिष्यान् उपदिशति।', hi: 'गुरु शिष्यों को उपदेश देते हैं।', en: 'The teacher instructs the disciples.' } }),
    supporting({ id: 'adis', root: 'आ + दिश्', iast: 'ā-diś', meaning: { hi: 'आदेश देना', en: 'to command' }, gana: 6, padaClass: 'parasmaipada', present: 'आदिश', imperfect: 'आदिश', future: ['आदेक्ष्य'], conditional: ['आदेक्ष्य'], upasarga: 'आ', example: { sa: 'राजा सेवकम् आदिशति।', hi: 'राजा सेवक को आदेश देता है।', en: 'The king commands the servant.' } }),
    supporting({ id: 'upahas', root: 'उप + हस्', iast: 'upa-has', meaning: { hi: 'उपहास करना', en: 'to mock' }, gana: 1, padaClass: 'parasmaipada', present: 'उपहस', imperfect: 'उपाहस', future: ['उपहसिष्य'], conditional: ['उपाहसिष्य'], upasarga: 'उप', baseRootId: 'has', example: { sa: 'मूर्खः अन्यान् उपहसति।', hi: 'मूर्ख दूसरों का उपहास करता है।', en: 'The fool mocks others.' } }),
    supporting({ id: 'vihas', root: 'वि + हस्', iast: 'vi-has', meaning: { hi: 'मुस्कुराना, हँसना', en: 'to smile, to laugh' }, gana: 1, padaClass: 'parasmaipada', present: 'विहस', imperfect: 'व्यहस', future: ['विहसिष्य'], conditional: ['व्यहसिष्य'], upasarga: 'वि', baseRootId: 'has', example: { sa: 'माता विहसति।', hi: 'माँ मुस्कुराती है।', en: 'Mother smiles.' } }),
    supporting({ id: 'prabhu', root: 'प्र + भू', iast: 'pra-bhū', meaning: { hi: 'समर्थ होना, उत्पन्न होना', en: 'to be able, to arise' }, gana: 1, padaClass: 'parasmaipada', present: 'प्रभव', imperfect: 'प्राभव', future: ['प्रभविष्य'], conditional: ['प्राभविष्य'], upasarga: 'प्र', baseRootId: 'bhu', example: { sa: 'गङ्गा हिमालयात् प्रभवति।', hi: 'गंगा हिमालय से निकलती है।', en: 'The Gaṅgā arises from the Himālaya.' } }),
    supporting({ id: 'sambhu', root: 'सम् + भू', iast: 'sam-bhū', meaning: { hi: 'सम्भव होना', en: 'to be possible, to happen' }, gana: 1, padaClass: 'parasmaipada', present: 'सम्भव', imperfect: 'समभव', future: ['सम्भविष्य'], conditional: ['समभविष्य'], upasarga: 'सम्', baseRootId: 'bhu', example: { sa: 'एतत् सम्भवति।', hi: 'यह सम्भव है।', en: 'This is possible.' } }),
    supporting({ id: 'parabhu', root: 'परा + भू', iast: 'parā-bhū', meaning: { hi: 'हराना', en: 'to defeat' }, gana: 1, padaClass: 'parasmaipada', present: 'पराभव', imperfect: 'पराभव', future: ['पराभविष्य'], conditional: ['पराभविष्य'], upasarga: 'परा', baseRootId: 'bhu', example: { sa: 'रामः रावणं पराभवति।', hi: 'राम रावण को हराते हैं।', en: 'Rāma defeats Rāvaṇa.' } }),
    supporting({ id: 'prastha', root: 'प्र + स्था', iast: 'pra-sthā', meaning: { hi: 'प्रस्थान करना', en: 'to set out, to depart' }, gana: 1, padaClass: 'atmanepada', present: 'प्रतिष्ठ', imperfect: 'प्रातिष्ठ', future: ['प्रस्थास्य'], conditional: ['प्रास्थास्य'], upasarga: 'प्र', baseRootId: 'stha', example: { sa: 'यात्रिकः प्रातः प्रतिष्ठते।', hi: 'यात्री सुबह प्रस्थान करता है।', en: 'The traveller sets out in the morning.' }, note: { hi: 'प्र के बाद स्था आत्मनेपदी (समवप्रविभ्यः स्थः 1.3.22)।', en: 'After प्र, स्था takes ātmanepada (समवप्रविभ्यः स्थः 1.3.22).' } })
]

/** Non-thematic supporting roots with stored tables. */
export const EXPLICIT_SUPPORTING: Dhatu[] = [
    {
        id: 'da',
        root: 'दा',
        iast: 'dā',
        meaning: { hi: 'देना', en: 'to give' },
        citation: 'ददाति',
        dhatupatha: 'डुदाञ् दाने',
        gana: 3,
        padaClass: 'ubhayapada',
        core: false,
        future: ['दास्य'],
        explicit: {
            lat: { P: tbl('ददाति', 'दत्तः', 'ददति', 'ददासि', 'दत्थः', 'दत्थ', 'ददामि', 'दद्वः', 'दद्मः') },
            lan: { P: tbl('अददात्', 'अदत्ताम्', 'अददुः', 'अददाः', 'अदत्तम्', 'अदत्त', 'अददाम्', 'अदद्व', 'अदद्म') },
            lot: { P: tbl('ददातु/दत्तात्', 'दत्ताम्', 'ददतु', 'देहि/दत्तात्', 'दत्तम्', 'दत्त', 'ददानि', 'ददाव', 'ददाम') },
            vidhilin: { P: tbl('दद्यात्', 'दद्याताम्', 'दद्युः', 'दद्याः', 'दद्यातम्', 'दद्यात', 'दद्याम्', 'दद्याव', 'दद्याम') }
        },
        explicitStatus: { lat: 'verified', lan: 'verified', lot: 'verified', vidhilin: 'verified' },
        presentDerivation: {
            expr: 'दा → ददा / दद्',
            note: {
                hi: 'जुहोत्यादिगण: धातु का द्वित्व होता है (ददा-); निर्बल रूपों में आ लुप्त होकर दद्- बनता है।',
                en: 'Class 3 (juhotyādi): the root is reduplicated (ददा-); in weak forms the आ is lost, giving दद्-.'
            }
        },
        related: [
            { sa: 'दानम्', meaning: { hi: 'दान', en: 'gift, giving' } },
            { sa: 'दाता', meaning: { hi: 'देने वाला', en: 'giver' } }
        ],
        examples: [{ sa: 'माता पुत्राय फलं ददाति।', hi: 'माँ पुत्र को फल देती है।', en: 'Mother gives a fruit to her son.' }],
        notes: [{ hi: 'जिसे दिया जाए वह चतुर्थी में आता है।', en: 'The receiver stands in चतुर्थी (dative).' }],
        status: 'verified',
        sources: ['dhatupatha', 'kale', 'macdonell']
    },
    {
        id: 'jna',
        root: 'ज्ञा',
        iast: 'jñā',
        meaning: { hi: 'जानना', en: 'to know' },
        citation: 'जानाति',
        dhatupatha: 'ज्ञा अवबोधने',
        gana: 9,
        padaClass: 'parasmaipada',
        core: false,
        future: ['ज्ञास्य'],
        explicit: {
            lat: { P: tbl('जानाति', 'जानीतः', 'जानन्ति', 'जानासि', 'जानीथः', 'जानीथ', 'जानामि', 'जानीवः', 'जानीमः') },
            lan: { P: tbl('अजानात्', 'अजानीताम्', 'अजानन्', 'अजानाः', 'अजानीतम्', 'अजानीत', 'अजानाम्', 'अजानीव', 'अजानीम') },
            lot: { P: tbl('जानातु', 'जानीताम्', 'जानन्तु', 'जानीहि', 'जानीतम्', 'जानीत', 'जानानि', 'जानाव', 'जानाम') },
            vidhilin: { P: tbl('जानीयात्', 'जानीयाताम्', 'जानीयुः', 'जानीयाः', 'जानीयातम्', 'जानीयात', 'जानीयाम्', 'जानीयाव', 'जानीयाम') }
        },
        explicitStatus: { lat: 'verified', lan: 'verified', lot: 'verified', vidhilin: 'verified' },
        presentDerivation: {
            expr: 'ज्ञा + ना → जाना / जानी',
            note: {
                hi: 'क्र्यादिगण: विकरण ना; ज्ञा का जा हो जाता है (ज्ञाजनोर्जा 7.3.79)। निर्बल रूपों में ना का नी।',
                en: 'Class 9 (kryādi): the marker ना; ज्ञा becomes जा (ज्ञाजनोर्जा 7.3.79). In weak forms ना becomes नी.'
            }
        },
        related: [
            { sa: 'ज्ञानम्', meaning: { hi: 'ज्ञान', en: 'knowledge' } },
            { sa: 'विज्ञानम्', meaning: { hi: 'विज्ञान', en: 'science' } }
        ],
        examples: [{ sa: 'अहं संस्कृतं जानामि।', hi: 'मैं संस्कृत जानता हूँ।', en: 'I know Sanskrit.' }],
        notes: [],
        status: 'verified',
        sources: ['dhatupatha', 'kale', 'macdonell']
    },
    {
        id: 'sru',
        root: 'श्रु',
        iast: 'śru',
        meaning: { hi: 'सुनना', en: 'to hear, to listen' },
        citation: 'शृणोति',
        dhatupatha: 'श्रु श्रवणे',
        gana: 5,
        padaClass: 'parasmaipada',
        core: false,
        future: ['श्रोष्य'],
        explicit: {
            lat: { P: tbl('शृणोति', 'शृणुतः', 'शृण्वन्ति', 'शृणोषि', 'शृणुथः', 'शृणुथ', 'शृणोमि', 'शृणुवः/शृण्वः', 'शृणुमः/शृण्मः') },
            lan: { P: tbl('अशृणोत्', 'अशृणुताम्', 'अशृण्वन्', 'अशृणोः', 'अशृणुतम्', 'अशृणुत', 'अशृणवम्', 'अशृणुव/अशृण्व', 'अशृणुम/अशृण्म') },
            lot: { P: tbl('शृणोतु', 'शृणुताम्', 'शृण्वन्तु', 'शृणु', 'शृणुतम्', 'शृणुत', 'शृणवानि', 'शृणवाव', 'शृणवाम') },
            vidhilin: { P: tbl('शृणुयात्', 'शृणुयाताम्', 'शृणुयुः', 'शृणुयाः', 'शृणुयातम्', 'शृणुयात', 'शृणुयाम्', 'शृणुयाव', 'शृणुयाम') }
        },
        explicitStatus: { lat: 'verified', lan: 'verified', lot: 'verified', vidhilin: 'verified' },
        presentDerivation: {
            expr: 'श्रु → शृ + नु → शृणो / शृणु',
            note: {
                hi: 'स्वादिगण: विकरण नु; श्रु का शृ हो जाता है (श्रुवः शृ च 3.1.74) और न का ण।',
                en: 'Class 5 (svādi): the marker नु; श्रु becomes शृ (श्रुवः शृ च 3.1.74) and न becomes ण.'
            }
        },
        related: [
            { sa: 'श्रवणम्', meaning: { hi: 'सुनना', en: 'listening' } },
            { sa: 'श्रोता', meaning: { hi: 'सुनने वाला', en: 'listener' } }
        ],
        examples: [{ sa: 'वयं कथां शृणुमः।', hi: 'हम कहानी सुनते हैं।', en: 'We listen to a story.' }],
        notes: [{ hi: 'उत्तम पुरुष द्विवचन और बहुवचन में दो स्वीकृत रूप हैं।', en: 'The first-person dual and plural each have two accepted forms.' }],
        status: 'verified',
        sources: ['dhatupatha', 'kale', 'macdonell']
    },
    {
        id: 'svap',
        root: 'स्वप्',
        iast: 'svap',
        meaning: { hi: 'सोना', en: 'to sleep' },
        citation: 'स्वपिति',
        dhatupatha: 'ञिष्वप् शये',
        gana: 2,
        padaClass: 'parasmaipada',
        core: false,
        future: ['स्वप्स्य'],
        explicit: {
            lat: { P: tbl('स्वपिति', 'स्वपितः', 'स्वपन्ति', 'स्वपिषि', 'स्वपिथः', 'स्वपिथ', 'स्वपिमि', 'स्वपिवः', 'स्वपिमः') }
        },
        explicitStatus: { lat: 'verified' },
        presentDerivation: {
            expr: 'स्वप् + इ + ति',
            note: {
                hi: 'अदादिगण: कोई विकरण नहीं; व्यञ्जनादि प्रत्ययों से पहले इ जुड़ता है (रुदादिभ्यः सार्वधातुके 7.2.76)।',
                en: 'Class 2: no class marker; an इ is inserted before consonant-initial endings (रुदादिभ्यः सार्वधातुके 7.2.76).'
            }
        },
        related: [{ sa: 'स्वप्नः', meaning: { hi: 'सपना', en: 'dream' } }],
        examples: [{ sa: 'शिशुः शय्यायां स्वपिति।', hi: 'शिशु बिस्तर पर सोता है।', en: 'The baby sleeps on the bed.' }],
        notes: [],
        status: 'verified',
        sources: ['dhatupatha', 'kale']
    },
    {
        id: 'ya',
        root: 'या',
        iast: 'yā',
        meaning: { hi: 'जाना, प्राप्त होना', en: 'to go, to reach' },
        citation: 'याति',
        dhatupatha: 'या प्रापणे',
        gana: 2,
        padaClass: 'parasmaipada',
        core: false,
        future: ['यास्य'],
        explicit: {
            lat: { P: tbl('याति', 'यातः', 'यान्ति', 'यासि', 'याथः', 'याथ', 'यामि', 'यावः', 'यामः') }
        },
        explicitStatus: { lat: 'verified' },
        presentDerivation: {
            expr: 'या + ति',
            note: { hi: 'अदादिगण: प्रत्यय सीधे धातु से जुड़ता है।', en: 'Class 2: the ending joins the root directly.' }
        },
        related: [{ sa: 'यात्रा', meaning: { hi: 'यात्रा', en: 'journey' } }],
        examples: [{ sa: 'विनयात् याति पात्रताम्।', hi: 'विनय से योग्यता प्राप्त होती है।', en: 'From humility one reaches worthiness.' }],
        notes: [],
        status: 'verified',
        sources: ['dhatupatha', 'kale']
    },
    {
        id: 'ap',
        root: 'आप्',
        iast: 'āp',
        meaning: { hi: 'पाना', en: 'to obtain' },
        citation: 'आप्नोति',
        dhatupatha: 'आप्लृ व्याप्तौ',
        gana: 5,
        padaClass: 'parasmaipada',
        core: false,
        future: ['आप्स्य'],
        explicit: {
            lat: { P: tbl('आप्नोति', 'आप्नुतः', 'आप्नुवन्ति', 'आप्नोषि', 'आप्नुथः', 'आप्नुथ', 'आप्नोमि', 'आप्नुवः', 'आप्नुमः') }
        },
        explicitStatus: { lat: 'verified' },
        presentDerivation: {
            expr: 'आप् + नु → आप्नो / आप्नु',
            note: { hi: 'स्वादिगण: विकरण नु।', en: 'Class 5: the marker नु.' }
        },
        related: [],
        examples: [{ sa: 'पात्रत्वात् धनम् आप्नोति।', hi: 'योग्यता से धन मिलता है।', en: 'From worthiness one obtains wealth.' }],
        notes: [],
        status: 'verified',
        sources: ['dhatupatha', 'kale']
    },
    {
        id: 'sak',
        root: 'शक्',
        iast: 'śak',
        meaning: { hi: 'सकना, समर्थ होना', en: 'to be able' },
        citation: 'शक्नोति',
        dhatupatha: 'शक्लृ शक्तौ',
        gana: 5,
        padaClass: 'parasmaipada',
        core: false,
        future: ['शक्ष्य'],
        explicit: {
            lat: { P: tbl('शक्नोति', 'शक्नुतः', 'शक्नुवन्ति', 'शक्नोषि', 'शक्नुथः', 'शक्नुथ', 'शक्नोमि', 'शक्नुवः', 'शक्नुमः') }
        },
        explicitStatus: { lat: 'verified' },
        presentDerivation: {
            expr: 'शक् + नु → शक्नो / शक्नु',
            note: {
                hi: 'स्वादिगण: विकरण नु। तुमुन्-प्रत्ययान्त के साथ प्रयोग: पठितुं शक्नोति (पढ़ सकता है)।',
                en: 'Class 5: the marker नु. Used with an infinitive: पठितुं शक्नोति (he can read).'
            }
        },
        related: [{ sa: 'शक्तिः', meaning: { hi: 'शक्ति', en: 'power, ability' } }],
        examples: [{ sa: 'अहं पठितुं शक्नोमि।', hi: 'मैं पढ़ सकता हूँ।', en: 'I can read.' }],
        notes: [],
        status: 'verified',
        sources: ['dhatupatha', 'kale']
    }
]

export const ALL_DHATUS: Dhatu[] = [...DHATUS, ...SUPPORTING_DHATUS, ...EXPLICIT_SUPPORTING]

export const CORE_DHATUS: Dhatu[] = DHATUS

const BY_ID = new Map(ALL_DHATUS.map(d => [d.id, d]))

export const getDhatu = (id: string): Dhatu | undefined => BY_ID.get(id)

export const padasOf = (d: Dhatu): Pada[] => {
    if (d.padaClass === 'ubhayapada') {
        const out: Pada[] = []
        if (d.present?.P || d.explicit?.lat?.P) out.push('P')
        if (d.present?.A || d.explicit?.lat?.A) out.push('A')
        return out.length > 0 ? out : ['P']
    }

    return d.padaClass === 'atmanepada' ? ['A'] : ['P']
}
